'use client';

import { useEffect } from 'react';

/**
 * Replicates Safari's back-swipe gesture for iOS standalone webapp mode.
 * Detects swipes starting from the left edge of the screen and calls history.back().
 * Self-disables when not running in standalone (home screen) mode.
 */
export function SwipeBack() {
  useEffect(() => {
    // Only activate in standalone webapp mode
    const isStandalone =
      ('standalone' in navigator && (navigator as Record<string, unknown>).standalone === true) ||
      window.matchMedia('(display-mode: standalone)').matches;

    if (!isStandalone) return;

    const EDGE_ZONE = 25;       // px from left edge to start tracking
    const MIN_DISTANCE = 80;    // px horizontal swipe to trigger back
    const MAX_RATIO = 0.6;      // max vertical/horizontal ratio (keep it horizontal)

    let tracking = false;
    let startX = 0;
    let startY = 0;

    function onTouchStart(e: TouchEvent) {
      const touch = e.touches[0];
      if (touch.clientX <= EDGE_ZONE) {
        tracking = true;
        startX = touch.clientX;
        startY = touch.clientY;
      }
    }

    function onTouchEnd(e: TouchEvent) {
      if (!tracking) return;
      tracking = false;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);

      // Must swipe right, far enough, and mostly horizontal
      if (dx >= MIN_DISTANCE && (dx === 0 || dy / dx <= MAX_RATIO)) {
        history.back();
      }
    }

    function onTouchCancel() {
      tracking = false;
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchCancel);
    };
  }, []);

  return null;
}
